import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, errMsg } from "../lib/api";
import { gbp } from "../lib/format";
import { Logo } from "../components/Logo";
import { Loading } from "../components/common";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { ApprovalFields } from "../components/pipeline/Decision";

function Card({ info }) {
  if (info.approval) return (
    <div className="rounded-lg border bg-card p-5">
      <div className="eyebrow">{info.kind_label}</div>
      <div className="mt-1 font-semibold" data-testid="email-action-title">{info.title}</div>
      <ul className="mt-2 space-y-1 text-sm text-muted-foreground">{(info.summary || []).map((s, i) => <li key={i}>{s}</li>)}</ul>
      {info.flags?.length > 0 && <div className="mt-2 text-sm font-semibold text-primary" data-testid="email-action-flags">{info.flags.join(", ")}</div>}
    </div>
  );
  return (
    <div className="rounded-lg border bg-card p-5">
      <div className="eyebrow">{info.ref} · {info.partner_name}</div>
      <div className="mt-1 font-semibold">{info.project_name}</div>
      <div className="mt-2 font-figure">{gbp(info.totals.net)} net · {gbp(info.totals.gross)} gross</div>
      <div className="mt-1 text-sm text-muted-foreground" data-testid="email-action-flags">{info.classification ? `${info.classification} · ` : ""}{info.flags.length ? info.flags.map((f) => `${f.label}${f.amount ? ` ${gbp(f.amount)}` : ""}`).join(", ") : "Clean – no flags"}</div>
      {info.variance_explanation && <p className="mt-3 text-sm" data-testid="email-action-variance"><b>Variance explanation:</b> {info.variance_explanation}</p>}
    </div>
  );
}

export default function EmailAction() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [reason, setReason] = useState("");
  const [extra, setExtra] = useState({ pot: "AM", owner_code: "", third_party: [] });
  const [err, setErr] = useState("");
  useEffect(() => { api.get(`/email-actions/${token}`).then((r) => setInfo(r.data)).catch(() => setInfo({ state: "expired", message: "This link has expired — please use the dashboard" })); }, [token]);
  if (!info) return <Loading />;
  const approve = info.action === "approve";
  const project = info.approval && info.kind === "project";
  const go = async () => { setErr(""); try { setInfo((await api.post(`/email-actions/${token}`, { reason, extra: project && approve ? extra : {} })).data); } catch (e) { setErr(errMsg(e)); } };
  return (
    <div className="mx-auto max-w-md px-5 py-12" data-testid="email-action-page" data-state={info.state}>
      <Logo className="h-8 mb-10" />
      {info.state === "expired" && <h1 className="font-heading text-2xl font-bold" data-testid="email-action-message">{info.message}</h1>}
      {info.ref && <Card info={info} />}
      {info.state === "decided" && <div className="mt-6"><h1 className="font-heading text-2xl font-bold" data-testid="email-action-message">{info.message}</h1>
        <p className="mt-2 text-sm" data-testid="email-action-outcome">Outcome: <b>{info.status}</b>{info.decision ? ` by ${info.decision.by} via ${info.decision.channel}` : ""}</p></div>}
      {info.state === "done" && <h1 className="mt-6 font-heading text-2xl font-bold" data-testid="email-action-message">Done – {info.status}</h1>}
      {info.state === "ready" && <div className="mt-6 space-y-3">
        <h1 className="font-heading text-2xl font-bold">{approve ? "Confirm approval" : info.approval ? "Reject" : "Reject this drawdown"}</h1>
        {project && approve && <ApprovalFields value={extra} onChange={setExtra} ownerCodes={info.owner_codes || []} />}
        {!approve && <Textarea placeholder="Reason (required)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="email-reject-reason" />}
        {err && <div className="text-sm font-medium text-primary" data-testid="email-action-error">{err}</div>}
        <Button className={`h-12 w-full ${approve ? "bg-[hsl(var(--positive))]" : ""}`} variant={approve ? "default" : "destructive"} onClick={go} data-testid="email-action-confirm">{approve ? "Approve" : "Reject"}</Button>
      </div>}
    </div>
  );
}
